import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $dashboardSummaryStore } from '@lib/store';
import { AmountUtils, cn, CurrencyUtils, PaletteUtils } from '@shared/utils';
import { Item } from './item';
import { View } from '@widgets';
import { Text } from '@shared/widgets';

interface CurrenciesProps {
  className?: string;
}

export const DashboardCurrencies = (props: CurrenciesProps) => {
  const dashboardSummaryStore = useUnit($dashboardSummaryStore.store);

  const { t } = useTranslation('dashboard');

  useEffect(() => {
    $dashboardSummaryStore.events.getSummaries();
    return () => $dashboardSummaryStore.events.clear();
  }, []);

  return (
    <div
      className={cn(
        'flex flex-col',
        PaletteUtils.isInsigniaPalette && 'gap-6',
        PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
        props.className,
      )}
    >
      <Text
        className={cn(PaletteUtils.isVcfcPalette && 'mb-4')}
        weight={PaletteUtils.isVcfcPalette ? 'semibold' : 'medium'}
        size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}
      >
        {t('card.currency.title', { Count: PaletteUtils.isInsigniaPalette ? `(${dashboardSummaryStore.data.length})` : '' })}
      </Text>
      <View
        loading={dashboardSummaryStore.loading}
        error={dashboardSummaryStore.error}
        noData={!dashboardSummaryStore.data.length}
        onRefresh={() => $dashboardSummaryStore.events.getSummaries()}
      >
        {dashboardSummaryStore.data.map((item, idx) => {
          const symbol = CurrencyUtils.getSymbolByCode(item.currency);
          return (
            <div
              key={idx}
              className={cn(
                'flex bg-white',
                PaletteUtils.isInsigniaPalette && 'pr-12 dark:bg-soft-black',
                PaletteUtils.isVcfcPalette && 'my-6 items-center dark:bg-light/5',
              )}
            >
              <div className='pr-12'>
                {PaletteUtils.isInsigniaPalette && (
                  <div
                    className={cn(
                      'relative h-[90px] w-[92px] shrink-0 grow-0 basis-auto overflow-hidden bg-soft-black dark:bg-slate',
                      'before:absolute before:left-1/2 before:top-1/2 before:-ml-3 before:mt-[6px] before:-translate-x-1/2 before:-translate-y-1/2 before:font-[Prata] before:text-[47px] before:leading-[1] before:text-primary before:content-[attr(data-icon)]',
                      'after:absolute after:-bottom-[17px] after:right-[8px] after:font-[Prata] after:text-[62px] after:leading-[1] after:text-primary after:opacity-[0.05] after:content-[attr(data-icon)]',
                      PaletteUtils.isVcfcPalette && [
                        'bg-[#374a61]',
                        'before:bg-[linear-gradient(180deg,_#d3c39f1a_0%,_#d3c39f00_100%)] before:text-white',
                        'after:bg-[linear-gradient(180deg,_#d3c39f1a_0%,_#d3c39f00_100%)] after:text-white',
                      ],
                    )}
                    data-icon={symbol}
                  >
                    <div className='absolute bottom-[-2px] left-[calc(100%-2px)] h-[150%] w-1/2 origin-bottom-left rotate-[-20deg] border-l border-primary bg-white dark:bg-soft-black' />
                  </div>
                )}
                {PaletteUtils.isVcfcPalette && (
                  <div className='flex h-[72px] w-[72px] items-center justify-center rounded-[100px] bg-primary'>
                    <Text size='3xl' color='white'>
                      {CurrencyUtils.getSymbolByCode(item.currency)}
                    </Text>
                  </div>
                )}
              </div>
              <div
                className={cn('flex w-full items-center justify-around gap-6 py-4', 'max-[1100px]:grid max-[1100px]:grid-cols-1 max-[1100px]:gap-3')}
              >
                <Item label={t('card.currency.fields.accounts')} value={item.accounts} />
                <Item label={t('card.currency.fields.cards')} value={item.cards} />
                <Item
                  label={t('card.currency.fields.availableBalance')}
                  value={AmountUtils.transformToAmountWithCurrency(item.availableBalance, item.currency)}
                />
                <Item label={t('card.currency.fields.amountDue')} value={AmountUtils.transformToAmountWithCurrency(item.amountDue, item.currency)} />
                <Item
                  label={t('card.currency.fields.totalLimit')}
                  value={AmountUtils.transformToAmountWithCurrency(item.totalLimit, item.currency)}
                />
              </div>
            </div>
          );
        })}
      </View>
    </div>
  );
};
